/**
 * AccountPage - User account settings with billing status and subscription management.
 * Route: /account
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import billingService from '../services/billingService';
import insightService from '../services/insightService';

const REPORT_TYPES = [
  { key: 'work', ja: '仕事・キャリア' },
  { key: 'romance', ja: '恋愛・パートナーシップ' },
  { key: 'relationships', ja: '人間関係' },
  { key: 'stress', ja: 'ストレス・メンタルケア' },
  { key: 'growth', ja: '自己成長' },
  { key: 'compatibility', ja: '相性分析' },
];

const Container = styled.div`
  max-width: 640px;
  margin: 0 auto;
  padding: 8px 16px 48px;
  @media (min-width: 640px) { padding: 24px; }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
`;

const BackLink = styled(Link)`
  color: ${(p) => p.theme.colors.textMuted};
  text-decoration: none;
  font-size: 1.2rem;
  &:hover { color: ${(p) => p.theme.colors.text}; }
`;

const PageTitle = styled.h1`
  font-size: 1.1rem;
  font-weight: 700;
  color: ${(p) => p.theme.colors.text};
  margin: 0;
`;

const SuccessBanner = styled.div`
  background: rgba(16, 185, 129, 0.1);
  border: 1px solid rgba(16, 185, 129, 0.3);
  border-radius: ${(p) => p.theme.borderRadius.md};
  padding: 12px 16px;
  margin-bottom: 24px;
  color: #10B981;
  font-size: 0.88rem;
  font-weight: 600;
`;

const CancelBanner = styled.div`
  background: rgba(136, 136, 168, 0.1);
  border: 1px solid rgba(136, 136, 168, 0.3);
  border-radius: ${(p) => p.theme.borderRadius.md};
  padding: 12px 16px;
  margin-bottom: 24px;
  color: ${(p) => p.theme.colors.textSecondary};
  font-size: 0.88rem;
`;

const WarningBanner = styled.div`
  background: rgba(245, 158, 11, 0.1);
  border: 1px solid rgba(245, 158, 11, 0.3);
  border-radius: ${(p) => p.theme.borderRadius.md};
  padding: 12px 16px;
  margin-bottom: 24px;
  color: #F59E0B;
  font-size: 0.88rem;
  font-weight: 600;
`;

const Section = styled.div`
  background: ${(p) => p.theme.colors.bgTertiary};
  border-radius: ${(p) => p.theme.borderRadius.lg};
  border: 1px solid ${(p) => p.theme.colors.border};
  padding: 24px;
  margin-bottom: 24px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
`;

const SectionTitle = styled.h2`
  font-size: 1rem;
  font-weight: 700;
  color: ${(p) => p.theme.colors.text};
  margin: 0;
`;

const ProBadge = styled.span`
  padding: 4px 12px;
  background: rgba(230, 199, 126, 0.15);
  color: ${(p) => p.theme.colors.primary};
  font-weight: 700;
  font-size: 0.78rem;
  border-radius: 9999px;
`;

const PlanInfo = styled.div`
  font-size: 0.85rem;
  color: ${(p) => p.theme.colors.textSecondary};
  margin-bottom: 16px;
  line-height: 1.6;
`;

const PortalButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: ${(p) => p.theme.colors.bgPrimary};
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: ${(p) => p.theme.borderRadius.md};
  color: ${(p) => p.theme.colors.textSecondary};
  font-size: 0.85rem;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.2s;
  &:hover {
    color: ${(p) => p.theme.colors.text};
    background: ${(p) => p.theme.colors.bgHover};
  }
`;

const FreeDesc = styled.p`
  font-size: 0.85rem;
  color: ${(p) => p.theme.colors.textMuted};
  margin: 0 0 16px 0;
`;

const ProFeatures = styled.div`
  background: ${(p) => p.theme.colors.bgPrimary};
  border-radius: ${(p) => p.theme.borderRadius.md};
  padding: 16px;
  margin-bottom: 16px;
`;

const FeatureTitle = styled.h3`
  font-size: 0.88rem;
  font-weight: 700;
  color: ${(p) => p.theme.colors.text};
  margin: 0 0 12px 0;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  color: ${(p) => p.theme.colors.textSecondary};
  margin-bottom: 8px;
  &::before {
    content: '';
    display: inline-block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${(p) => p.theme.colors.primary};
    flex-shrink: 0;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  @media (min-width: 480px) { flex-direction: row; }
`;

const PrimaryBtn = styled.button`
  flex: 1;
  padding: 10px 16px;
  background: ${(p) => p.theme.colors.primary};
  color: #fff;
  border: none;
  border-radius: ${(p) => p.theme.borderRadius.md};
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: opacity 0.2s;
  &:hover { opacity: 0.85; }
`;

const ReportLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px;
  background: ${(p) => p.theme.colors.bgPrimary};
  border-radius: ${(p) => p.theme.borderRadius.md};
  border: 1px solid ${(p) => p.theme.colors.border};
  text-decoration: none;
  transition: background 0.15s;
  margin-bottom: 8px;
  &:hover { background: ${(p) => p.theme.colors.bgHover}; }
`;

const ReportName = styled.span`
  flex: 1;
  font-size: 0.85rem;
  color: ${(p) => p.theme.colors.text};
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.85rem;
  margin-bottom: 8px;
`;

const InfoLabel = styled.span`
  color: ${(p) => p.theme.colors.textMuted};
`;

const InfoValue = styled.span`
  color: ${(p) => p.theme.colors.text};
  font-family: ${(p) => p.theme.fonts.mono};
`;

/* ========== Extended profile (v3) ========== */
const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 14px;
`;

const FieldRow2 = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  @media (max-width: ${(p) => p.theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const FieldLabel = styled.label`
  font-family: ${(p) => p.theme.fonts.mono};
  font-size: 0.68rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${(p) => p.theme.colors.textMuted};
`;

const Input = styled.input`
  width: 100%;
  padding: 11px 14px;
  background: ${(p) => p.theme.colors.bgPrimary};
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: 9px;
  color: ${(p) => p.theme.colors.text};
  font-size: 0.92rem;
  font-family: ${(p) => p.theme.fonts.main};
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.2s, box-shadow 0.2s;
  &:focus {
    border-color: ${(p) => p.theme.colors.primary};
    box-shadow: 0 0 0 3px ${(p) => p.theme.colors.primaryGlow};
  }
  &::placeholder { color: ${(p) => p.theme.colors.textMuted}; }
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 96px;
  padding: 12px 14px;
  background: ${(p) => p.theme.colors.bgPrimary};
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: 9px;
  color: ${(p) => p.theme.colors.text};
  font-size: 0.92rem;
  font-family: ${(p) => p.theme.fonts.main};
  line-height: 1.7;
  outline: none;
  resize: vertical;
  box-sizing: border-box;
  transition: border-color 0.2s, box-shadow 0.2s;
  &:focus {
    border-color: ${(p) => p.theme.colors.primary};
    box-shadow: 0 0 0 3px ${(p) => p.theme.colors.primaryGlow};
  }
`;

const InterestRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const InterestChip = styled.button`
  padding: 7px 13px;
  background: ${(p) => (p.$active ? p.theme.colors.primaryGlow : 'transparent')};
  border: 1px solid ${(p) => (p.$active ? p.theme.colors.lineGold : p.theme.colors.lineSoft)};
  border-radius: 9999px;
  color: ${(p) => (p.$active ? p.theme.colors.starGold : p.theme.colors.textMuted)};
  font-size: 0.8rem;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.2s;
  &:hover { color: ${(p) => p.theme.colors.text}; }
`;

const ContextLevelRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const ContextLevelOption = styled.button`
  padding: 9px 14px;
  background: ${(p) => (p.$active ? p.theme.colors.primaryGlow : 'transparent')};
  border: 1px solid ${(p) => (p.$active ? p.theme.colors.lineGold : p.theme.colors.lineSoft)};
  border-radius: 10px;
  color: ${(p) => (p.$active ? p.theme.colors.starGold : p.theme.colors.textSecondary)};
  font-size: 0.85rem;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  &:hover { color: ${(p) => p.theme.colors.text}; }
`;

const ContextLevelTitle = styled.span`
  font-weight: 600;
`;

const ContextLevelHint = styled.span`
  font-size: 0.7rem;
  color: ${(p) => p.theme.colors.textMuted};
`;

const SaveButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 11px 22px;
  background: ${(p) => p.theme.colors.gradGold};
  color: #1A1408;
  border: 1px solid rgba(230,199,126,0.55);
  border-radius: 9999px;
  font-size: 0.88rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.25s;
  margin-top: 8px;
  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: ${(p) => p.theme.shadows.glow};
  }
  &:disabled { opacity: 0.45; cursor: not-allowed; transform: none; }
`;

const SaveHint = styled.span`
  font-size: 0.78rem;
  color: ${(p) => p.theme.colors.starMint};
  margin-left: 10px;
`;

const FieldDesc = styled.p`
  margin: 0 0 14px;
  font-size: 0.82rem;
  line-height: 1.85;
  color: ${(p) => p.theme.colors.textSecondary};
`;

const INTEREST_TOPICS = [
  { key: 'love', ja: '恋愛' },
  { key: 'work', ja: '仕事' },
  { key: 'relationships', ja: '人間関係' },
  { key: 'flow', ja: 'いまの流れ' },
  { key: 'future', ja: 'これから' },
  { key: 'self', ja: '自分を読む' },
];

const CONTEXT_LEVELS = [
  { key: 'none',  ja: '最低限', hint: 'タイプコードのみ' },
  { key: 'basic', ja: '標準',   hint: 'タイプ + 軸スコア' },
  { key: 'full',  ja: '詳細',   hint: '上に加え 出生情報・メモ' },
];

function AccountPage() {
  const { t } = useLanguage();
  const { isAuthenticated, user } = useAuth();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showCancel, setShowCancel] = useState(false);

  // 拡張プロフィール (v3)
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [profile, setProfile] = useState({
    display_name: '',
    birth_date: '',
    birth_time: '',
    birth_place: '',
    occupation: '',
    interests: [],
  });
  const [selfMemo, setSelfMemo] = useState('');
  const [contextLevel, setContextLevel] = useState('basic');
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileSavedAt, setProfileSavedAt] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) return;
    let alive = true;
    insightService.getProfile()
      .then((data) => {
        if (!alive) return;
        const pe = data.profile_extended || {};
        setProfile({
          display_name: pe.display_name || '',
          birth_date: pe.birth_date || '',
          birth_time: pe.birth_time || '',
          birth_place: pe.birth_place || '',
          occupation: pe.occupation || '',
          interests: Array.isArray(pe.interests) ? pe.interests : [],
        });
        setSelfMemo(data.self_memo || '');
        setContextLevel(data.ai_context_level || 'basic');
      })
      .catch(() => { /* 未認証や未作成は無視 */ })
      .finally(() => { if (alive) setProfileLoaded(true); });
    return () => { alive = false; };
  }, [isAuthenticated]);

  const toggleInterest = (key) => {
    setProfile((prev) => {
      const has = prev.interests.includes(key);
      return {
        ...prev,
        interests: has
          ? prev.interests.filter((k) => k !== key)
          : [...prev.interests, key],
      };
    });
  };

  const saveProfile = async () => {
    setProfileSaving(true);
    try {
      await insightService.patchProfile({
        profile_extended: { ...profile },
        self_memo: selfMemo,
        ai_context_level: contextLevel,
      });
      setProfileSavedAt(new Date());
    } catch (e) {
      // フォームエラー表示はシンプルに
      alert(t('保存に失敗しました。再度お試しください。', 'Save failed. Please retry.'));
    } finally {
      setProfileSaving(false);
    }
  };

  const fetchStatus = useCallback(() => {
    return billingService.getIndividualStatus().then((data) => {
      setStatus(data);
      return data;
    });
  }, []);

  useEffect(() => {
    const checkoutParam = searchParams.get('checkout');
    if (checkoutParam === 'success') {
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 8000);

      // webhook を待つだけだと、配送失敗時に「決済したのに Pro にならない」
      // 状態から復旧できない。Stripe に直接照会して確定させてから状態を読む。
      const sessionId = searchParams.get('session_id');
      let cancelled = false;
      (async () => {
        if (sessionId) {
          try {
            await billingService.reconcileCheckout(sessionId);
          } catch {
            // 失敗しても webhook 側で処理される可能性があるのでポーリングは続ける
          }
        }
        if (cancelled) return;
        for (let attempt = 0; attempt < 3; attempt++) {
          try {
            const data = await fetchStatus();
            if (cancelled || data?.plan === 'pro') return;
          } catch {
            if (cancelled) return;
          }
          await new Promise((r) => setTimeout(r, 2000));
        }
      })();
      return () => { cancelled = true; };
    } else if (checkoutParam === 'cancel') {
      setShowCancel(true);
      setTimeout(() => setShowCancel(false), 5000);
    }
  }, [searchParams, fetchStatus]);

  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    fetchStatus()
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [isAuthenticated, fetchStatus]);

  const handlePortal = async () => {
    try {
      const { portal_url } = await billingService.createIndividualPortal();
      window.location.href = portal_url;
    } catch (err) {
      alert(err.message || 'Failed to open portal');
    }
  };

  const handleCheckout = async (planType) => {
    try {
      const { checkout_url } = await billingService.createIndividualCheckout(planType);
      window.location.href = checkout_url;
    } catch (err) {
      alert(err.message || 'Failed to create checkout');
    }
  };

  if (!isAuthenticated) {
    return (
      <Container>
        <p style={{ textAlign: 'center', color: '#8888a8', padding: '48px 0' }}>
          {t('ログインしてください', 'Please log in')}
        </p>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container>
        <p style={{ textAlign: 'center', color: '#8888a8', padding: '48px 0' }}>
          {t('読み込み中...', 'Loading...')}
        </p>
      </Container>
    );
  }

  const isPro = status?.plan === 'pro';
  const hasIndividualSub = status?.status === 'active' || status?.status === 'past_due';

  return (
    <Container>
      <Header>
        <BackLink to="/">&larr;</BackLink>
        <PageTitle>{t('アカウント設定', 'Account Settings')}</PageTitle>
      </Header>

      {showSuccess && (
        <SuccessBanner>{t('契約が完了しました！反映までしばらくお待ちください。', 'Subscription activated! It may take a moment to reflect.')}</SuccessBanner>
      )}

      {showCancel && (
        <CancelBanner>{t('チェックアウトがキャンセルされました。', 'Checkout was canceled.')}</CancelBanner>
      )}

      {status?.status === 'past_due' && (
        <WarningBanner>
          {t(
            'お支払いに問題があります。サブスクリプション管理からお支払い情報を更新してください。',
            'There is a payment issue. Please update your payment information in Subscription Management.'
          )}
        </WarningBanner>
      )}

      {/* Current Plan */}
      <Section>
        <SectionHeader>
          <SectionTitle>{t('現在のプラン', 'Current Plan')}</SectionTitle>
        </SectionHeader>

        {isPro ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <ProBadge>Pro</ProBadge>
              {hasIndividualSub && (
                <span style={{ fontSize: '0.85rem', color: '#8888a8' }}>
                  {t('月額プラン', 'Monthly')}
                </span>
              )}
            </div>
            {hasIndividualSub ? (
              <PlanInfo>
                {t('ステータス', 'Status')}: <span style={{ color: '#E6C77E', fontWeight: 600 }}>{status?.status}</span>
                {status?.current_period_end && (
                  <><br />{t('次回更新日', 'Next renewal')}: {new Date(status.current_period_end).toLocaleDateString('ja-JP')}</>
                )}
              </PlanInfo>
            ) : (
              <PlanInfo>
                {t('ご招待プランで全機能が利用可能です。', 'Full access via invited plan.')}
              </PlanInfo>
            )}
            {hasIndividualSub && (
              <PortalButton onClick={handlePortal}>
                {t('サブスクリプション管理', 'Manage Subscription')}
              </PortalButton>
            )}
          </>
        ) : (
          <>
            <FreeDesc>
              {t(
                '無料プランをご利用中です。Proプランにアップグレードすると、全機能が利用可能になります。',
                'You are on the Free plan. Upgrade to Pro to unlock all features.'
              )}
            </FreeDesc>
            <ProFeatures>
              <FeatureTitle>{t('Proプランの特典', 'Pro Benefits')}</FeatureTitle>
              <FeatureList>
                <FeatureItem>{t('全6カテゴリのレポート閲覧', 'All 6 category reports')}</FeatureItem>
                <FeatureItem>{t('AIチャット無制限', 'Unlimited AI chat')}</FeatureItem>
                <FeatureItem>{t('何度でも再診断可能', 'Unlimited re-diagnosis')}</FeatureItem>
              </FeatureList>
            </ProFeatures>
            <ButtonGroup>
              <PrimaryBtn onClick={() => handleCheckout('monthly')}>
                {t('月額 ¥980', '¥980/month')}
              </PrimaryBtn>
            </ButtonGroup>
          </>
        )}
      </Section>

      {/* Purchased Reports */}
      {status && status.purchased_reports && status.purchased_reports.length > 0 && (
        <Section>
          <SectionHeader>
            <SectionTitle>{t('購入済みレポート', 'Purchased Reports')}</SectionTitle>
          </SectionHeader>
          {status.purchased_reports.map((reportKey) => {
            const info = REPORT_TYPES.find((r) => r.key === reportKey);
            return (
              <ReportLink key={reportKey} to={`/report/${reportKey}`}>
                <ReportName>{info?.ja || reportKey}</ReportName>
              </ReportLink>
            );
          })}
        </Section>
      )}

      {/* Extended profile (v3) — チャット/Insightの個別化に使う */}
      <Section>
        <SectionHeader>
          <SectionTitle>{t('プロフィール', 'Profile')}</SectionTitle>
        </SectionHeader>
        <FieldDesc>
          {t(
            '入力した情報は、チャットとテーマ別の読み解きに使われます。共有レベルは下で選べます。',
            'These details personalise chat and topical readings. Choose your sharing level below.'
          )}
        </FieldDesc>

        <Field>
          <FieldLabel>{t('表示名', 'Display name')}</FieldLabel>
          <Input
            value={profile.display_name}
            onChange={(e) => setProfile({ ...profile, display_name: e.target.value })}
            placeholder={t('呼ばれたい名前', 'How you would like to be called')}
          />
        </Field>

        <FieldRow2>
          <Field>
            <FieldLabel>{t('生年月日', 'Birth date')}</FieldLabel>
            <Input
              type="date"
              value={profile.birth_date}
              onChange={(e) => setProfile({ ...profile, birth_date: e.target.value })}
            />
          </Field>
          <Field>
            <FieldLabel>{t('出生時刻 (任意)', 'Birth time (optional)')}</FieldLabel>
            <Input
              type="time"
              value={profile.birth_time}
              onChange={(e) => setProfile({ ...profile, birth_time: e.target.value })}
            />
          </Field>
        </FieldRow2>

        <Field>
          <FieldLabel>{t('出生地 (任意)', 'Birth place (optional)')}</FieldLabel>
          <Input
            value={profile.birth_place}
            onChange={(e) => setProfile({ ...profile, birth_place: e.target.value })}
            placeholder={t('例: 東京都港区', 'e.g. Tokyo, Minato')}
          />
        </Field>

        <Field>
          <FieldLabel>{t('職業 / 役割 (任意)', 'Role / occupation (optional)')}</FieldLabel>
          <Input
            value={profile.occupation}
            onChange={(e) => setProfile({ ...profile, occupation: e.target.value })}
            placeholder={t('例: デザイナー / 経営者', 'e.g. Designer / Founder')}
          />
        </Field>

        <Field>
          <FieldLabel>{t('関心テーマ', 'Interests')}</FieldLabel>
          <InterestRow>
            {INTEREST_TOPICS.map((tp) => (
              <InterestChip
                key={tp.key}
                type="button"
                $active={profile.interests.includes(tp.key)}
                onClick={() => toggleInterest(tp.key)}
              >
                {tp.ja}
              </InterestChip>
            ))}
          </InterestRow>
        </Field>

        <Field>
          <FieldLabel>{t('いまの自分について (自由メモ)', 'About yourself now (free memo)')}</FieldLabel>
          <Textarea
            value={selfMemo}
            onChange={(e) => setSelfMemo(e.target.value)}
            maxLength={500}
            placeholder={t('500 字以内で。チャットや診断のコンテキストに使われます。', 'Up to 500 chars. Used as chat / insight context.')}
          />
        </Field>

        <Field>
          <FieldLabel>{t('AI との共有レベル', 'AI sharing level')}</FieldLabel>
          <ContextLevelRow>
            {CONTEXT_LEVELS.map((cl) => (
              <ContextLevelOption
                key={cl.key}
                type="button"
                $active={contextLevel === cl.key}
                onClick={() => setContextLevel(cl.key)}
              >
                <ContextLevelTitle>{cl.ja}</ContextLevelTitle>
                <ContextLevelHint>{cl.hint}</ContextLevelHint>
              </ContextLevelOption>
            ))}
          </ContextLevelRow>
        </Field>

        <div>
          <SaveButton type="button" onClick={saveProfile} disabled={!profileLoaded || profileSaving}>
            {profileSaving ? t('保存中…', 'Saving…') : t('プロフィールを保存', 'Save profile')}
          </SaveButton>
          {profileSavedAt && (
            <SaveHint>{t('保存しました', 'Saved')}</SaveHint>
          )}
        </div>
      </Section>

      {/* Account Info */}
      <Section>
        <SectionTitle style={{ marginBottom: 16 }}>{t('アカウント情報', 'Account Info')}</SectionTitle>
        <InfoRow>
          <InfoLabel>{t('メール', 'Email')}</InfoLabel>
          <InfoValue>{user?.email || '-'}</InfoValue>
        </InfoRow>
        <InfoRow>
          <InfoLabel>{t('名前', 'Name')}</InfoLabel>
          <InfoValue>{user?.name || '-'}</InfoValue>
        </InfoRow>
      </Section>
    </Container>
  );
}

export default AccountPage;
